import { useRouter } from 'next/router';

import { useOptionalCriteriaSaveBatch } from '../contexts/CriteriaSaveBatchContext';
import { usePropertyEditMutation } from './usePropertyEditMutation';
import {
  type SetListingCriteriaMutationVariables,
  useSetListingCriteriaMutation,
} from './useSetListingCriteriaMutation';

type UseCriteriaSaveMutationOptions = {
  /** Used only when there is no `CriteriaSaveBatchProvider` above the caller. */
  successToastMessage: string;
};

/**
 * The criteria save for a single field.
 *
 * Under a `CriteriaSaveBatchProvider` this returns the shared batched save, so sibling fields
 * collapse into one `/criteria/set`, and the toast and meta come from the provider. The standalone
 * mutation below is the fallback for fields rendered without a provider, and it is what every
 * criteria field used to do on its own.
 */
export const useCriteriaSaveMutation = ({ successToastMessage }: UseCriteriaSaveMutationOptions) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const batch = useOptionalCriteriaSaveBatch();

  const standaloneMutation = usePropertyEditMutation({
    mutationHook: useSetListingCriteriaMutation,
    successToastMessage,
    meta: {
      invalidates: ['users-properties-criteria-get'],
      context: { api: 'userPropertyCriteriaSet', details: { listingAlias } },
    },
  });

  if (!batch) {
    return { mutation: standaloneMutation, isBatched: false };
  }

  const batchedMutation = {
    ...standaloneMutation,
    mutateAsync: (variables: SetListingCriteriaMutationVariables) => batch.enqueue(variables),
    isPending: batch.isPending,
    error: batch.error,
  } as unknown as typeof standaloneMutation;

  return { mutation: batchedMutation, isBatched: true };
};
